import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { Check, X, ArrowRight, RotateCcw, ExternalLink, Lock } from "lucide-react";
import { TV, TvKicker, TvHeader } from "../components/TriviaTheme";
import { ShareScore } from "../components/ShareScoreCard";
import { ShareYearCard } from "../components/ShareYearCard";
import { CHART_NAMES } from "../components/ChartRounds";
import { recordDailyPlay, milestoneFor, CHART_KEY, SPORT_KEY } from "../lib/streak";
import { deviceId } from "../lib/device";
import { ChartLeaderboard, SportLeaderboard, useIsChampion } from "../components/ChartLeaderboard";
import { Flame } from "lucide-react";

const LABELS = ["A", "B", "C", "D"];
const QUESTION_SECONDS = 45;

export default function TriviaQuiz() {
  const [params] = useSearchParams();
  const source = params.get("source") || "category";
  const category = params.get("category") || "";
  const difficulty = parseInt(params.get("difficulty") || "0", 10);
  const count = parseInt(params.get("count") || "10", 10);
  const mode = params.get("mode") === "purist" ? "purist" : "mc";
  const group = params.get("group") === "1";
  const date = params.get("date") || "";
  const pack = params.get("pack") || "";
  const decade = parseInt(params.get("decade") || "0", 10);
  const artist = params.get("artist") || "";
  const region = params.get("region") || "";
  const sport = params.get("sport") || "";
  const sportEvent = params.get("event") || "";
  const chart = params.get("chart") || "";
  const chartName = CHART_NAMES[`${region || "uk"}-${chart || "singles"}`];
  const nav = useNavigate();
  const { consume, requireMember, isMember, access } = useVisitor();
  const accessReady = access !== null;
  const startedFor = useRef("");

  const [blocked, setBlocked] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [purist, setPurist] = useState("");
  const [finished, setFinished] = useState(false);
  const [marks, setMarks] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [boardTick, setBoardTick] = useState(0);
  const champion = useIsChampion(isSportDailySrc(params) ? "sport" : (params.get("region") || "uk"));
  const [streak, setStreak] = useState(null);
  const [seconds, setSeconds] = useState(QUESTION_SECONDS);
  const inputRef = useRef(null);

  const isChartDaily = source === "chart-daily";
  const isSportDaily = source === "sport-daily";
  const todayIso = new Date().toISOString().slice(0, 10);
  const isToday = !date || date === todayIso;
  const fetchQuestions = () =>
    (source === "daily"
      ? api.get("/trivia/daily", { params: { date, mode } })
      : isChartDaily ? api.get("/trivia/chart-daily", { params: { region: region || "uk", mode, date: date || undefined } })
      : isSportDaily ? api.get("/trivia/sport-daily", { params: { date: date || undefined } })
      : api.get("/trivia/quiz", { params: { category, difficulty: difficulty || undefined, count, mode, pack: pack || undefined, decade: decade || undefined, artist: artist || undefined, region: region || undefined, chart: chart || undefined, sport: sport || undefined, event: sportEvent || undefined } }))
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => setQuestions([]));

  useEffect(() => {
    const gated = (source === "daily" || isChartDaily || isSportDaily) && !isToday;
    if (gated && !accessReady) return; // wait for Club status before gating archive replays
    const key = [source, category, difficulty, count, date].join("|");
    if (startedFor.current === key) return;
    startedFor.current = key;
    (async () => {
      // Archive replays (a past daily) are Club-only; today's Daily Three is free.
      if (gated) {
        if (!requireMember({ title: "The archive is Club-only", message: "Replay any past Daily Three, Chart Three or Sport Three with CurioVault Club — plus unlimited quizzes and host mode." })) {
          setBlocked(true); setQuestions([]); return;
        }
      }
      // A full category quiz uses one of the free daily plays (members unlimited).
      if (source === "category" || source === "pack") {
        const ok = await consume("quiz");
        if (!ok) { setBlocked(true); setQuestions([]); return; }
      }
      fetchQuestions();
    })();
    /* eslint-disable-next-line */
  }, [source, category, difficulty, count, date, accessReady]);

  const q = questions && questions[idx];
  const total = questions ? questions.length : 0;
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (finished && source === "daily" && (!date || date === today) && total > 0) setStreak(recordDailyPlay());
    if (finished && isChartDaily && isToday && total > 0) {
      setStreak(recordDailyPlay(CHART_KEY));
      api.post("/trivia/chart-daily/submit", { region: region || "uk", device: deviceId(), answers })
        .catch(() => {}).finally(() => setBoardTick((n) => n + 1));
    }
    if (finished && isSportDaily && isToday && total > 0) {
      setStreak(recordDailyPlay(SPORT_KEY));
      api.post("/trivia/sport-daily/submit", { region: "sport", device: deviceId(), answers })
        .catch(() => {}).finally(() => setBoardTick((n) => n + 1));
    }
    // eslint-disable-next-line
  }, [finished, source, date, total, isChartDaily]);
  const progress = useMemo(() => (total ? ((idx + (finished ? 1 : 0)) / total) * 100 : 0), [idx, total, finished]);

  // Per-question countdown — pauses once locked, auto-locks at zero.
  useEffect(() => {
    if (!q || locked || finished) return;
    if (seconds <= 0) { submitAnswer(mode === "purist" ? purist.trim() : selected); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [seconds, q, locked, finished]);

  const submitAnswer = async (answer) => {
    if (locked || !q) return;
    setLocked(true);
    try {
      const { data } = await api.post("/trivia/check", { id: q.id, answer: answer || "" });
      setResult(data);
      if (data.correct) setScore((s) => s + 1);
      setMarks((m) => [...m, !!data.correct]);
      setAnswers((a) => [...a, { id: q.id, answer: answer || "" }]);
      if (q.items?.length) setRounds((r) => [...r, { items: q.items, guess: answer || "", correct: data.correct_answer, ok: !!data.correct }]);
    } catch {
      setResult({ correct: false, correct_answer: "", fun_fact: "", sources: [] });
    }
  };

  const next = () => {
    if (idx + 1 < total) {
      setIdx((i) => i + 1);
      setLocked(false); setSelected(null); setResult(null); setPurist(""); setSeconds(QUESTION_SECONDS);
    } else {
      setFinished(true);
    }
  };

  const restart = () => {
    setIdx(0); setScore(0); setMarks([]); setRounds([]); setAnswers([]); setLocked(false); setSelected(null);
    setResult(null); setPurist(""); setFinished(false); setQuestions(null); setSeconds(QUESTION_SECONDS);
    fetchQuestions();
  };

  const optClass = (opt) => {
    if (!locked) {
      return opt === selected
        ? "bg-[#161514] border-[#161514] text-white shadow-[0_12px_28px_-12px_rgba(0,0,0,0.45)]"
        : "bg-[#F7F4EE] border-[#E4DED2] text-[#161514] hover:bg-[#EEE8DD]";
    }
    if (result && opt === result.correct_answer) return "bg-[#2D4A3E] border-[#2D4A3E] text-white";
    if (opt === selected) return "bg-[#F5E4DF] border-[#E7C3B9] text-[#7E3628]";
    return "bg-[#F7F4EE] border-[#ECE6DB] text-[#A9A399]";
  };

  const letterChip = (opt) => {
    if (!locked) return opt === selected ? "bg-white text-[#161514]" : "bg-white text-[#57524B] border border-[#DDD5C7]";
    if (result && opt === result.correct_answer) return "bg-white text-[#2D4A3E]";
    if (opt === selected) return "bg-white text-[#9E4535] border border-[#E7C3B9]";
    return "bg-white text-[#BDB5A8] border border-[#ECE6DB]";
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const titleSize = group ? "text-2xl sm:text-4xl" : "text-lg sm:text-2xl";
  const regionTag = (region || "uk").toUpperCase();
  const isSport = pack === "sporting-moments";
  const packTitle = isSport ? (sportEvent || sport || "Sporting Moments")
    : pack === "box-office" ? `Box Office${decade ? ` · ${decade}s` : ""}`
    : pack === "battles" ? "Battles of History"
    : pack === "christmas-number-ones" ? `${regionTag} Christmas No.1s`
    : pack === "name-the-year" ? "Name the Year"
    : pack === "artist-rounds" ? `${artist} No.1s`
    : pack ? `${regionTag} ${chart === "albums" ? "Album" : "Singles"} No.1s${decade ? ` · ${decade}s` : ""}` : "";
  const chartThree = `${regionTag} Chart Three`;
  const headerTitle = source === "daily" ? "Daily Three." : isChartDaily ? `${chartThree}.` : isSportDaily ? "Sport Three." : `${packTitle || category || "Trivia"}.`;
  const kicker = source === "daily"
    ? `Daily · ${new Date((date || new Date().toISOString().slice(0, 10)) + "T00:00:00Z")
        .toLocaleDateString("en-GB", { month: "short", day: "numeric", timeZone: "UTC" })}`
    : (isChartDaily || isSportDaily) ? `${isChartDaily ? "Daily chart round" : "Daily sport round"} · ${new Date((date || todayIso) + "T00:00:00Z").toLocaleDateString("en-GB", { month: "short", day: "numeric", timeZone: "UTC" })}`
    : isSport ? "Special round · Sporting moments"
    : (pack === "box-office" || pack === "battles") ? "Special round"
    : pack ? `Special round · ${chartName}` : "Category round";
  const Btn = "inline-flex items-center justify-center gap-2 font-ui font-semibold text-sm rounded-full transition-[transform,background-color,opacity] active:scale-[0.98]";

  return (
    <Layout>
      <div className={`font-ui ${TV.bg} min-h-screen`}>
        <TvHeader title="Trivia Vault" subtitle={group ? "Host mode · read it to the room" : "Exit to the hub"} isMember={isMember} back="/trivia" />
        <div className={`mx-auto px-4 sm:px-6 ${group ? "max-w-4xl" : "max-w-2xl"}`}>
          <div className="pt-6 sm:pt-10">
            <TvKicker>{kicker}</TvKicker>
            <div className="flex items-end justify-between gap-3 mt-3">
              <div className="min-w-0">
                <h1 className="font-ui font-bold text-[#161514] text-[28px] sm:text-4xl leading-[1.05]">{headerTitle}</h1>
                <p className="font-ui text-sm text-[#77716A] mt-1">{total || count} questions{group ? " · host mode" : ""}</p>
              </div>
              <span data-testid="quiz-score" className="shrink-0 inline-flex items-center gap-2 bg-white border border-[#E4DED2] rounded-full px-3 py-1.5">
                <span className="tv-caps !text-[9px] text-[#77716A]">Score</span>
                <span className="font-ui text-sm font-bold text-[#161514] leading-none tabular-nums">{score}<span className="text-[#BDB5A8]">/{total || "–"}</span></span>
                <span className="w-px h-4 bg-[#E4DED2]" />
                {!finished && q ? (
                  <span data-testid="timer-pill" className={`font-ui text-xs font-semibold tabular-nums ${seconds <= 10 && !locked ? "text-[#9E4535]" : "text-[#57524B]"}`}>{mmss}</span>
                ) : (
                  <span className="font-ui text-sm font-semibold text-[#BDB5A8] leading-none">···</span>
                )}
              </span>
            </div>
            <div className="w-full h-1 bg-[#E4DED2] rounded-full overflow-hidden mt-5" data-testid="quiz-progress-bar">
              <div className="h-full bg-[#2D4A3E] rounded-full transition-[width] duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="pb-24 sm:pb-14 pt-5">
            {questions === null && <p className="text-center font-ui text-[#A9A399] py-24">Loading…</p>}

            {questions && total === 0 && (
              <div className={`${TV.card} p-8 text-center`}>
                {blocked ? (
                  <>
                    <span className="w-10 h-10 mx-auto rounded-full bg-[#2D4A3E] flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></span>
                    <p className="font-ui text-sm sm:text-base text-[#57524B] mt-3">This one's for Club members (or you've used today's free plays).</p>
                    <Link to="/club" data-testid="quiz-blocked-club" className={`${Btn} ${TV.pillDark} mt-5 px-5 py-2.5`}>Join the Club</Link>
                    <Link to="/trivia" className="block mt-4 font-ui text-sm font-semibold text-[#77716A] hover:text-[#161514] transition-colors">Back to the Vault</Link>
                  </>
                ) : (
                  <>
                    <p className="font-ui text-[#57524B]">No questions found for this selection.</p>
                    <Link to="/trivia" className="inline-block mt-4 font-ui font-semibold text-[#161514] underline">Back to the Vault</Link>
                  </>
                )}
              </div>
            )}

            {finished && (
              <div data-testid="quiz-finished" className={`${TV.card} overflow-hidden tv-rise`}>
                <div className="p-5 sm:p-7">
                  <div className="flex items-center justify-between gap-3">
                    <p className="tv-caps text-[#57524B]">Result</p>
                    <span className={`${TV.pillMint} font-ui text-xs font-medium px-3 py-1`}>{source === "daily" ? "Daily Three" : isChartDaily ? chartThree : isSportDaily ? "Sport Three" : packTitle || category || "Round"} complete</span>
                  </div>
                  {streak && <MilestoneSeal count={streak.count} />}
                  {streak && (
                    <p data-testid="streak-badge" className={`${TV.pillMint} inline-flex items-center gap-1.5 mt-4 px-3 py-1 font-ui text-xs font-semibold`}>
                      <Flame className="w-3.5 h-3.5" /> {streak.count}-day {isChartDaily ? "chart " : isSportDaily ? "sport " : ""}streak{streak.best > streak.count ? ` · best ${streak.best}` : streak.count > 1 ? " · personal best" : ""}
                    </p>
                  )}
                  <h2 className="font-ui font-bold text-[#161514] text-5xl sm:text-6xl tracking-[-0.04em] leading-none mt-5">
                    {score} <span className="text-[#BDB5A8]">/ {total}.</span>
                  </h2>
                  <p className="font-ui text-[15px] sm:text-base text-[#77716A] mt-3 leading-relaxed">
                    {score === total ? <><b className="text-[#161514] font-semibold">Flawless.</b> The pub owes you a pint.</> : score / total >= 0.5 ? <><b className="text-[#161514] font-semibold">Solid round</b>, a good pub-team result.</> : <><b className="text-[#161514] font-semibold">Tough one.</b> Try another round.</>}
                  </p>
                  <div className={`${TV.inset} p-4 sm:p-5 mt-5`}>
                    <p className="font-ui font-semibold text-sm text-[#161514]">Ready for another?</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                      <button data-testid="quiz-replay" onClick={restart} className={`${Btn} ${TV.pillDark} px-5 py-2.5`}><RotateCcw className="w-4 h-4" /> Play again</button>
                      <button data-testid="quiz-new" onClick={() => nav("/trivia")} className={`${Btn} ${TV.pillLight} px-5 py-2.5`}>New quiz</button>
                      {pack === "name-the-year" && rounds.length > 0 && <ShareYearCard rounds={rounds} chartName={chartName} />}
                      {source === "daily" && <ShareScore score={score} total={total} marks={marks} date={date} streak={streak?.count || 0} />}
                      {isChartDaily && <ShareScore score={score} total={total} marks={marks} date={date} streak={streak?.count || 0} label={chartThree} champion={champion} />}
                      {isSportDaily && <ShareScore score={score} total={total} marks={marks} date={date} streak={streak?.count || 0} label="Sport Three" champion={champion} />}
                    </div>
                  </div>
                  {isChartDaily && isToday && boardTick > 0 && <div className="mt-4"><ChartLeaderboard refresh={boardTick} region={region || "uk"} /></div>}
                  {isSportDaily && isToday && boardTick > 0 && <div className="mt-4"><SportLeaderboard refresh={boardTick} /></div>}
                </div>
                <div className={`${TV.footer} px-5 sm:px-7 py-2.5 flex justify-between font-ui text-[11px] sm:text-xs text-[#77716A]`}>
                  <span>Mode: {mode === "mc" ? "multiple choice" : "purist"}</span><span>{Math.round((score / Math.max(total, 1)) * 100)}% correct</span>
                </div>
              </div>
            )}

            {!finished && q && (
              <div data-testid="question-card" key={idx} className={`${TV.card} overflow-hidden tv-rise`}>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="tv-caps text-[#57524B] shrink-0">Q{idx + 1} / {total}</span>
                      <span className={`${TV.pillTag} font-ui text-[11px] font-semibold px-2.5 py-1 truncate`}>{q.category}</span>
                    </div>
                    {locked && result && (
                      <span data-testid="answer-verdict" className={`${result.correct ? TV.pillMint : TV.pillBrick} inline-flex items-center gap-1 px-3 py-1 font-ui text-xs font-semibold shrink-0`}>
                        {result.correct ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {result.correct ? "Correct" : "Incorrect"}
                      </span>
                    )}
                  </div>

                  <h2 className={`font-ui font-bold text-[#161514] leading-snug tracking-[-0.015em] ${titleSize}`}>{q.question}</h2>

                  {mode === "mc" && (
                    <div className="grid gap-2 mt-5">
                      {q.options.map((opt, i) => (
                        <button
                          key={i}
                          data-testid={`option-card-${i}`}
                          disabled={locked}
                          onClick={() => setSelected(opt)}
                          className={`flex items-center gap-3 text-left rounded-[14px] border px-3 transition-[background-color,color,border-color,transform] active:scale-[0.99] ${group ? "py-4" : "py-2.5"} ${optClass(opt)}`}
                        >
                          <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-ui font-bold text-xs ${letterChip(opt)}`}>{LABELS[i]}</span>
                          <span className={`flex-1 min-w-0 font-ui font-medium ${group ? "text-lg" : "text-sm sm:text-[15px]"}`}>{opt}</span>
                          {locked && result && opt === result.correct_answer && <Check className="w-4 h-4 shrink-0 text-white" />}
                          {locked && opt === selected && !result?.correct && <X className="w-4 h-4 shrink-0 text-[#9E4535]" />}
                        </button>
                      ))}
                      {!locked && (
                        <button data-testid="lock-in-answer-button" disabled={selected === null} onClick={() => submitAnswer(selected)}
                          className={`${Btn} ${TV.pillDark} mt-2 w-full py-3 disabled:opacity-35 disabled:cursor-not-allowed`}>
                          <Lock className="w-4 h-4" /> Lock in answer
                        </button>
                      )}
                    </div>
                  )}

                  {mode === "purist" && (
                    <form className="mt-5" onSubmit={(e) => { e.preventDefault(); if (!locked && purist.trim()) submitAnswer(purist.trim()); }}>
                      <input
                        ref={inputRef}
                        data-testid="purist-input"
                        disabled={locked}
                        value={purist}
                        onChange={(e) => setPurist(e.target.value)}
                        placeholder="Type your answer…"
                        className={`w-full px-5 py-3 rounded-full border border-[#DDD5C7] bg-[#F7F4EE] font-ui text-[15px] text-[#161514] outline-none focus:border-[#2D4A3E] focus:bg-white transition-colors disabled:opacity-70 ${group ? "text-lg py-4" : ""}`}
                      />
                      {!locked && (
                        <button data-testid="lock-in-answer-button" type="submit" disabled={!purist.trim()} className={`${Btn} ${TV.pillDark} mt-2 w-full py-3 disabled:opacity-35`}>
                          <Lock className="w-4 h-4" /> Lock in answer
                        </button>
                      )}
                      {locked && result && (
                        <div className={`mt-3 px-4 py-3 rounded-[14px] border font-ui text-sm font-medium ${result.correct ? "bg-[#E5EEE8] border-[#C7D9CE] text-[#2D4A3E]" : "bg-[#F5E4DF] border-[#E7C3B9] text-[#7E3628]"}`}>
                          {result.correct ? "Correct!" : "Not quite."} Answer: <span className="font-bold">{result.correct_answer}</span>
                        </div>
                      )}
                    </form>
                  )}

                  {locked && result && (
                    <div data-testid="quiz-reveal" className="mt-5">
                      {result.fun_fact && (
                        <div data-testid="fun-fact-reveal-box" className={`${TV.green} !rounded-[16px] p-4 sm:p-5`}>
                          <p className="tv-caps !text-[11px] text-white/70">Fun fact</p>
                          <p className="font-ui text-sm sm:text-[15px] leading-relaxed text-white/90 mt-2">{result.fun_fact}</p>
                          {result.sources && result.sources.length > 0 && (
                            <div className={`${TV.greenInset} !rounded-[12px] px-4 py-3 mt-4`}>
                              <p className="font-ui font-semibold text-white text-xs mb-1.5">Sources</p>
                              <div className="flex flex-wrap gap-x-5 gap-y-2">
                                {result.sources.map((s, i) => (
                                  <a key={i} href={s} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-ui text-xs text-white/85 hover:text-white underline underline-offset-2 transition-colors">
                                    <ExternalLink className="w-3.5 h-3.5" /> Source {i + 1}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <button data-testid="quiz-next" onClick={next} className={`${Btn} ${TV.pillDark} mt-4 w-full py-3`}>
                        {idx + 1 < total ? "Next question" : "See results"} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className={`${TV.footer} px-4 sm:px-6 py-2.5 flex justify-between font-ui text-[11px] sm:text-xs text-[#77716A]`}>
                  <span>Mode: {mode === "mc" ? "multiple choice" : "purist"}</span>
                  <span>{locked ? "Answer locked" : "Pick, then lock in"}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

const isSportDailySrc = (params) => params.get("source") === "sport-daily";

const MilestoneSeal = ({ count }) => {
  const m = milestoneFor(count);
  if (!m.earned) return m.next && count > 0 ? (
    <p data-testid="milestone-next" className="mt-4 font-ui text-xs text-[#77716A]">{m.toNext} more day{m.toNext === 1 ? "" : "s"} to earn the {m.next}-day black seal.</p>
  ) : null;
  return (
    <div data-testid="milestone-seal" className={`mt-5 flex items-center gap-4 rounded-[16px] border p-3 sm:p-4 ${m.justHit ? "bg-[#161514] border-[#161514] text-white tv-rise" : "bg-[#F7F4EE] border-[#E4DED2] text-[#161514]"}`}>
      <img src="/seal-black-256.png" alt={`${m.earned}-day black seal`} width={64} height={64}
        className={`w-14 h-14 sm:w-16 sm:h-16 shrink-0 object-contain ${m.justHit ? "drop-shadow-[0_0_12px_rgba(255,255,255,0.35)] animate-[spin_1.2s_ease-out_1]" : ""}`} />
      <div className="min-w-0">
        <p className={`tv-caps !text-[10px] ${m.justHit ? "text-white/70" : "text-[#77716A]"}`}>{m.justHit ? "Milestone reached" : "Seal held"}</p>
        <p className="font-ui font-bold text-base sm:text-lg leading-tight">{m.earned}-Day Black Seal</p>
        <p className={`font-ui text-xs mt-0.5 ${m.justHit ? "text-white/75" : "text-[#77716A]"}`}>
          {m.justHit ? `${m.earned} Daily Threes in a row. Remarkable.` : m.next ? `${m.toNext} more day${m.toNext === 1 ? "" : "s"} to the ${m.next}-day seal.` : "The highest seal in the Vault."}
        </p>
      </div>
    </div>
  );
};
